import { isNil } from 'lodash';
import { ReactNode, useContext } from 'react';
import { Button } from '@shared/components/Button';
import { IconName } from '@shared/components/Icon';
import { View } from '@shared/components/View';
import { ColorThemeContext } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import classNames from './AnchoredSheetTitleBar.module.css';

interface Props {
  title?: string;
  leftIcon?: IconName;
  leftIconColor?: string;
  /** Icon size in grid units. */
  leftIconGridSize?: number;
  leftIconAnchorName?: string;
  leftTitlePosition?: number;
  onLeftIconPress?: VoidFunction;
  rightIcon?: IconName;
  rightIconColor?: string;
  /** Icon size in grid units. */
  rightIconGridSize?: number;
  rightButtonText?: string;
  rightTitlePosition?: number;
  titleSize?: number;
  onRightIconPress?: () => void;
  endContent?: ReactNode;
  showBorder?: boolean;
  height?: number;
}

const DEFAULT_ICON_GRID_SIZE = 5;

export function AnchoredSheetTitleBar({
  title,
  leftIcon,
  leftIconColor,
  leftIconGridSize,
  leftIconAnchorName,
  onLeftIconPress,
  rightIcon,
  rightIconColor,
  rightIconGridSize,
  rightButtonText,
  titleSize,
  onRightIconPress,
  endContent,
  showBorder = true,
  height,
}: Props) {
  const colorTheme = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const defaultTitleSize = isDesktop ? 16 : 20;

  return (
    <View className={[classNames.titleBar, showBorder && classNames.bordered]} style={{ height }}>
      <div className={classNames.left}>
        {leftIcon && onLeftIconPress && (
          <View
            paddingHorizontal={1.5}
            style={leftIconAnchorName ? { anchorName: leftIconAnchorName } : undefined}
          >
            <Button
              variant="ghost"
              iconOnly
              icon={leftIcon}
              iconGridSize={leftIconGridSize ?? DEFAULT_ICON_GRID_SIZE}
              vars={{ 'button-foreground': leftIconColor || colorTheme.foregroundActive }}
              onClick={onLeftIconPress}
            />
          </View>
        )}
      </div>

      <div className={classNames.title} style={{ fontSize: titleSize ?? defaultTitleSize }}>
        {title}
      </div>

      <div className={classNames.right}>
        {rightIcon && onRightIconPress && (
          <View paddingHorizontal={2}>
            <Button
              variant="ghost"
              iconOnly
              icon={rightIcon}
              iconGridSize={rightIconGridSize ?? DEFAULT_ICON_GRID_SIZE}
              vars={{ 'button-foreground': rightIconColor || colorTheme.foregroundActive }}
              onClick={onRightIconPress}
            />
          </View>
        )}
        {!isNil(onRightIconPress) && !isNil(rightButtonText) && !rightIcon && (
          <View paddingHorizontal={2}>
            <Button
              variant="primary"
              size="medium"
              onClick={onRightIconPress}
              label={rightButtonText}
            />
          </View>
        )}
        {endContent}
      </div>
    </View>
  );
}
